export const GUIDE_CATEGORIES = [
  { key: 'account', label: 'Account & Sign-In', blurb: 'Getting in, staying in, and keeping your account secure.' },
  { key: 'tree', label: 'Family Trees', blurb: 'Building, cleaning up, and protecting your tree.' },
  { key: 'dna', label: 'DNA', blurb: 'From kit activation to understanding matches.' },
  { key: 'updates', label: 'Apps & Updates', blurb: 'Keeping apps, browsers, and syncing in good shape.' },
];

export const GUIDES = [
  {
    slug: 'cant-sign-in-to-ancestry',
    category: 'account',
    title: 'Can’t Sign In? A Calm, Step-by-Step Checklist',
    summary: 'Work through the most common sign-in problems in order, from typos to cookies to verification codes.',
    readTime: '5 min read',
    sections: [
      {
        heading: 'Start with the simple things',
        body: [
          'Most sign-in problems come down to a small detail. Before resetting anything, check that Caps Lock is off, that you are typing the email address — not a username — and that there is no extra space at the end when you paste.',
          'If you have used more than one email address over the years, try each one. It is very common to find an account under an old work or internet-provider address.',
        ],
      },
      {
        heading: 'Reset your password the right way',
        steps: [
          'Choose “Forgot password” on the sign-in page.',
          'Enter the email address on the account and request one reset email only.',
          'Wait up to ten minutes and check spam, junk, and promotions folders.',
          'Open the newest email and use its link straight away — older links stop working.',
          'Choose a new password you have not used before and store it in a password manager.',
        ],
      },
      {
        heading: 'If the page just reloads',
        body: [
          'When you enter the right details and the page simply returns to the sign-in screen, your browser is usually blocking cookies. Allow cookies for the site, turn off privacy extensions for it, and try again in a fresh window.',
        ],
      },
    ],
  },
  {
    slug: 'two-step-verification-help',
    category: 'account',
    title: 'Two-Step Verification: Codes, Phones, and Lockouts',
    summary: 'What to do when codes do not arrive, you changed phone numbers, or you are stuck in a verification loop.',
    readTime: '4 min read',
    sections: [
      {
        heading: 'Why verification exists',
        body: [
          'Your account can hold decades of research and sensitive DNA data. Two-step verification means a stolen password alone is not enough to get in. It is worth the small extra step.',
        ],
      },
      {
        heading: 'When codes do not arrive',
        steps: [
          'Check your phone has signal and can receive texts from short-code numbers.',
          'Make sure your phone’s date and time are set automatically.',
          'Request one new code and use only that latest code.',
          'If text messages fail, choose email verification if it is offered.',
        ],
      },
      {
        heading: 'If you changed your phone number',
        body: [
          'Update your phone number in account settings while you can still sign in. If you are already locked out, you will need to go through the account recovery process, which may ask you to confirm details about your account. Our technicians can walk you through what to expect.',
        ],
      },
    ],
  },
  {
    slug: 'protect-your-ancestry-account',
    category: 'account',
    title: 'Keeping Your Account and Research Safe',
    summary: 'Simple habits that protect years of work: strong passwords, sharing settings, and regular backups.',
    readTime: '5 min read',
    sections: [
      {
        heading: 'Passwords and phishing',
        body: [
          'Use a unique password for your family history account. Be wary of emails asking you to “confirm your account” through a link — sign in by typing the address yourself instead.',
          'Genuine helps teams, including ours, will never ask for your password.',
        ],
      },
      {
        heading: 'Review who can see your tree',
        steps: [
          'Open your tree settings and check whether the tree is public or private.',
          'Review the list of people you have invited and their roles.',
          'Remove anyone who no longer needs access.',
          'Remember that living people are generally hidden from others by default — double-check if you have marked anyone incorrectly.',
        ],
      },
      {
        heading: 'Keep your own backup',
        body: [
          'Export your tree as a GEDCOM file every few months and save it somewhere safe, such as an external drive. Download treasured photos and stories too, because GEDCOM files do not include media.',
        ],
      },
    ],
  },
  {
    slug: 'fix-duplicate-people-in-tree',
    category: 'tree',
    title: 'Finding and Merging Duplicate People',
    summary: 'How duplicates sneak in, how to confirm two profiles are the same person, and how to merge safely.',
    readTime: '6 min read',
    sections: [
      {
        heading: 'How duplicates happen',
        body: [
          'Duplicates usually arrive when you accept a hint that adds a new person instead of attaching to an existing one, when you import a GEDCOM file into an existing tree, or when two people editing the same tree add the same ancestor.',
        ],
      },
      {
        heading: 'Confirm before you merge',
        body: [
          'Open both profiles side by side. Compare birth and death dates, places, parents, spouses, and children. Two people with the same name in the same town were very common — matching names alone is not proof.',
        ],
      },
      {
        heading: 'Merge carefully',
        steps: [
          'Export a GEDCOM backup of your tree first.',
          'Start the merge from the profile that has the most complete information.',
          'Review every field and choose which value to keep — do not accept defaults blindly.',
          'After merging, check the family view to confirm parents, spouses, and children are all attached correctly.',
        ],
      },
    ],
  },
  {
    slug: 'correct-wrong-relationships',
    category: 'tree',
    title: 'Fixing Wrong Parents, Spouses, and Children',
    summary: 'Untangle relationships without deleting people — and without losing the records attached to them.',
    readTime: '5 min read',
    sections: [
      {
        heading: 'Edit, don’t delete',
        body: [
          'When someone is attached to the wrong family, the instinct is to delete them and start again. That throws away attached records, photos, and notes. Editing the relationship keeps all of that intact.',
        ],
      },
      {
        heading: 'Moving a person to the right family',
        steps: [
          'Open the person’s profile and find the option to edit relationships.',
          'Remove the incorrect parent, spouse, or child link.',
          'Use “add existing person” to connect them to the correct relative instead of creating someone new.',
          'Check the relationship type — biological, adopted, step, or foster — is set correctly.',
        ],
      },
      {
        heading: 'Clean up records afterwards',
        body: [
          'Records accepted from hints sometimes carry the wrong family with them. Review each attached record and detach any that clearly belong to a different person.',
        ],
      },
    ],
  },
  {
    slug: 'get-better-search-results',
    category: 'tree',
    title: 'Getting Better Results From Record Searches',
    summary: 'Search smarter with spelling variations, date ranges, and places — and know when to narrow down.',
    readTime: '6 min read',
    sections: [
      {
        heading: 'Start broad, then narrow',
        body: [
          'Begin with a name, an approximate birth year, and a country. If you get thousands of results, add a county or town. If you get none, remove details one at a time.',
        ],
      },
      {
        heading: 'Expect spelling to wander',
        body: [
          'Clerks wrote names as they heard them, and transcribers read old handwriting as best they could. Search for sound-alike spellings, nicknames, initials, and maiden names. A surname like “Smyth” may appear as Smith, Smithe, or Smyht.',
        ],
      },
      {
        heading: 'Use what you already know',
        steps: [
          'Add a spouse or parent’s first name to filter results.',
          'Use a date range of plus or minus two to five years.',
          'Search specific collections — census, parish, military — when the general search is too noisy.',
          'Save promising results to the person’s profile with a note on why you think they match.',
        ],
      },
    ],
  },
  {
    slug: 'activate-ancestry-dna-kit',
    category: 'dna',
    title: 'Activating Your DNA Kit Correctly',
    summary: 'Make sure results land in the right account and are linked to the right person from day one.',
    readTime: '4 min read',
    sections: [
      {
        heading: 'Decide whose account it belongs in',
        body: [
          'Before activating, decide who will manage the results. If you are testing a parent, you can activate the kit in your own account and be listed as the manager — or the parent can have their own account. Moving results later is possible but harder.',
        ],
      },
      {
        heading: 'Activation steps',
        steps: [
          'Sign in to the account that should hold the results.',
          'Go to the DNA section and choose to activate a kit.',
          'Enter the activation code from the tube label exactly as printed.',
          'Confirm who took the test and complete the consent questions.',
          'Collect the sample following the kit instructions and post it promptly.',
        ],
      },
      {
        heading: 'Link results to your tree',
        body: [
          'Linking the DNA results to the test-taker’s profile in your family tree unlocks features that show how matches may connect to you. You can change the link later in DNA settings.',
        ],
      },
    ],
  },
  {
    slug: 'understanding-dna-matches',
    category: 'dna',
    title: 'Understanding Your DNA Match List',
    summary: 'Shared DNA, predicted relationships, and how to organise matches without feeling overwhelmed.',
    readTime: '7 min read',
    sections: [
      {
        heading: 'What the numbers mean',
        body: [
          'Each match shows how much DNA you share, usually measured in centimorgans (cM). More shared DNA means a closer relationship. The predicted relationship is an estimate — a 400 cM match could be a first cousin once removed, a half first cousin, or several other relationships.',
        ],
      },
      {
        heading: 'Organise with groups and notes',
        steps: [
          'Start with your closest matches and identify which side of the family they are on.',
          'Create coloured groups such as “Mum’s side” and “Dad’s side”.',
          'Use shared matches to place other matches into the same groups.',
          'Add notes recording what you know and what you have ruled out.',
        ],
      },
      {
        heading: 'Contacting matches',
        body: [
          'A short, friendly message works best: say how you think you might be related and what you are researching. Many people test only for ethnicity and may not reply — that is normal, not personal.',
        ],
      },
    ],
  },
  {
    slug: 'dna-ethnicity-estimate-updates',
    category: 'dna',
    title: 'Why Your Ethnicity Estimate Changed',
    summary: 'Estimate updates explained in plain English — and why a change does not mean anything went wrong.',
    readTime: '4 min read',
    sections: [
      {
        heading: 'Estimates improve over time',
        body: [
          'Your DNA does not change, but the reference data it is compared against does. As more people with deep roots in particular regions are added to reference panels, the estimate becomes more precise. That can move percentages between neighbouring regions.',
        ],
      },
      {
        heading: 'What stays the same',
        body: [
          'Your match list, notes, groups, and linked tree are generally unaffected by an estimate update. Close family matches remain the strongest evidence for research.',
        ],
      },
      {
        heading: 'Keep a record',
        steps: [
          'Take a screenshot of your current estimate before updates roll out.',
          'Read any explanation shown on your DNA page after an update.',
          'Compare regions rather than exact percentages — broad patterns are more reliable.',
        ],
      },
    ],
  },
  {
    slug: 'mobile-app-not-working',
    category: 'updates',
    title: 'Mobile App Crashing or Not Loading',
    summary: 'A step-by-step fix list for iPhone, iPad, and Android when the app freezes, crashes, or shows a blank screen.',
    readTime: '5 min read',
    sections: [
      {
        heading: 'Quick fixes first',
        steps: [
          'Fully close the app and reopen it.',
          'Restart your phone or tablet.',
          'Check for an app update in the App Store or Google Play.',
          'Install any waiting system update for your device.',
        ],
      },
      {
        heading: 'Clear the app’s stored data',
        body: [
          'On Android, open Settings › Apps, choose the app, then Storage › Clear cache. On iPhone and iPad, go to Settings › General › iPhone Storage, choose the app, and select Offload App, then reinstall it. Your tree lives online and is not deleted.',
        ],
      },
      {
        heading: 'If it still fails',
        body: [
          'Check whether the website works in a browser on the same device. If the website works but the app does not, the problem is with the app on that device. If neither works, the cause is likely your connection or account — our technicians can help narrow it down.',
        ],
      },
    ],
  },
  {
    slug: 'keep-browser-updated-for-ancestry',
    category: 'updates',
    title: 'Browser Settings That Keep Everything Working',
    summary: 'Updates, cookies, pop-ups, and extensions: the browser settings that matter most for genealogy websites.',
    readTime: '5 min read',
    sections: [
      {
        heading: 'Keep your browser current',
        body: [
          'Modern genealogy sites rely on up-to-date browser features for tree viewers, image zoom, and uploads. Chrome, Edge, Firefox, and Safari update themselves, but only after a restart — so close and reopen your browser regularly.',
        ],
      },
      {
        heading: 'Settings to check',
        steps: [
          'Allow cookies for the site so you stay signed in.',
          'Allow pop-ups for the site so record images and print views can open.',
          'Turn off ad blockers and script blockers for the site.',
          'If images look black or distorted, turn off hardware acceleration and restart.',
        ],
      },
      {
        heading: 'When to clear your cache',
        body: [
          'Clear the cache for the site when pages look broken after an update. You will need to sign in again afterwards, so make sure you know your password first.',
        ],
      },
    ],
  },
  {
    slug: 'sync-desktop-tree-software',
    category: 'updates',
    title: 'Syncing Desktop Tree Software With Your Online Tree',
    summary: 'Safe syncing habits for desktop genealogy programs — and what to do when a sync stalls or conflicts.',
    readTime: '6 min read',
    sections: [
      {
        heading: 'Back up before every sync',
        body: [
          'Syncing writes changes in both directions. If something goes wrong mid-sync, a backup is the only guaranteed way back. Make a backup in your desktop program before you press sync — every time.',
        ],
      },
      {
        heading: 'Update in the right order',
        steps: [
          'Install the latest update for your desktop genealogy software.',
          'Restart the computer.',
          'Open the program, make a backup, and then sync.',
          'Review the change log or conflict list before accepting changes.',
        ],
      },
      {
        heading: 'When a sync gets stuck',
        body: [
          'Leave a large first sync running — it can take a long time for big trees with many photos. If it fails repeatedly, note any error code, restart, and try again on a wired or strong connection. Repeated failures often point to a single problem person or media file, which a technician can help you find.',
        ],
      },
    ],
  },
];

export const guidePath = (slug) => `/guides/${slug}`;
export const findGuide = (slug) => GUIDES.find((g) => g.slug === slug);
export const guidesIn = (key) => GUIDES.filter((g) => g.category === key);
export const categoryLabel = (key) => GUIDE_CATEGORIES.find((c) => c.key === key)?.label || '';
